/*
 * ============================================================
 * CCS BUILDER HOST — assets/ccs-builder-host.js
 * ============================================================
 * The page the SIB shirt builder runs inside on ChromeCityShirts.
 *
 * The builder itself (assets/sb-builder.js) is SIB's, re-derived rather than
 * rewritten — see rederive-ccs.js at the root of this theme. It owns the
 * canvas, the tools, the selection handles, the history, the export, and the
 * mobile layout. None of that is reimplemented here, and none of it should be.
 *
 * This file owns the one thing SIB has no concept of: a LISTING, which is one
 * design across several colors, each color with its own artwork and its own
 * sizes.
 *
 * ── How the two fit together ───────────────────────────────────────────────
 * The builder holds one garment at a time. The host holds them all, and drives
 * the builder through them:
 *
 *      seller clicks a color in the rail
 *        -> host snapshots the canvas       CCSBuilder.document()
 *        -> host stores it against the old color
 *        -> host re-opens the builder on the new color's garment, with that
 *           color's stored scene                  CCSBuilder.open()
 *
 * Re-opening is not a workaround: openBuilder already disposes its canvases
 * and rebuilds from a garment plus a scene, because that is what it does
 * coming back from a login redirect. A color swap is the same operation.
 *
 * Publishing walks the same path once per color that has unexported work,
 * calling CCSBuilder.prepare() on each — which is the storefront's own
 * add-to-cart export, untouched: artwork cropped to the print area, the
 * fabric scene, and a composited preview, all uploaded through this file's
 * uploadArtwork.
 *
 * ── What the server is not told ────────────────────────────────────────────
 * Prices, GTINs and blank costs are never sent from here. The payload names a
 * style, color keys, size names and the uploaded file URLs; everything with
 * money or fulfilment attached to it is resolved server-side from the SIB
 * feed. See CCSServices/ccsApi.js.
 */
(function (window, document) {
  'use strict';

  var State = window.CCSBuilderState;
  var Api = window.CCSApi;
  var Upload = window.CCSUpload;

  var SIDES = ['front', 'back'];

  /*
   * Where closing the builder goes.
   *
   * The dashboard, because that is where the designs and the store details
   * are, and it copes with every state -- signed out, no store yet, or a
   * seller with listings -- so it is never the blank page /pages/sell was for
   * someone who already has a store.
   */
  var EXIT_URL = '/pages/store';

  var root = document.getElementById('ccs-builder-host');
  if (!root) return;

  var signedIn = root.getAttribute('data-logged-in') === 'true';

  // Read here rather than rendered into the page: Liquid cannot see the
  // query string.
  function param(name) {
    var match = window.location.search.match(new RegExp('[?&]' + name + '=([^&]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  // /pages/design?style=<sib-style-handle>, chosen by the shirt picker on
  // /pages/sell, or by one of the seller's own styles on /pages/store.
  var styleHandle = param('style');

  /*
   * Two ways in on an existing design, and the difference is what Save does.
   *
   *   ?from=<uid>   duplicate: a new listing, leaving the original alone
   *   ?edit=<uid>   edit: write over that listing, keeping its product
   *
   * Editing keeps the uid, so the product keeps its handle, its URL, its
   * place in the seller's categories and its order history. Duplicating is
   * for "a variation of this"; editing is for "this, but fixed".
   *
   * Either way the style comes from the listing, so ?style= is ignored.
   */
  var fromUid = param('from');
  var editUid = param('edit');
  var sourceUid = editUid || fromUid;
  var isEdit = !!editUid;

  var state = null;
  var style = null;
  // Set while the host is itself driving the builder from color to color.
  // Every snapshot/re-open pair has to be allowed to finish before another
  // starts, or two openBuilder calls race over the same canvases.
  var switching = false;
  var quoteToken = 0;

  function $(selector, scope) { return (scope || document).querySelector(selector); }
  function hook(name, scope) { return (scope || document).querySelector('[data-ccs="' + name + '"]'); }
  function hooks(name, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll('[data-ccs="' + name + '"]'));
  }

  function money(value) {
    var n = Number(value);
    if (!isFinite(n)) return '—';
    // Customer-facing prices are whole dollars by the time the server quotes
    // them, so cents here would only ever be ".00".
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  // ── Dialogs ───────────────────────────────────────────────────────────────
  // The builder's own `.sb-sub` pattern: a class, not a display style, so the
  // stylesheet keeps its bottom-sheet behaviour on a phone.

  function openDialog(id) { var el = document.getElementById(id); if (el) el.classList.add('open'); }
  function closeDialog(id) { var el = document.getElementById(id); if (el) el.classList.remove('open'); }

  function showError(element, text) {
    if (!element) return;
    element.textContent = text || '';
    element.classList.toggle('show', !!text);
  }

  function message(title, body) {
    var titleEl = hook('msg-title');
    var bodyEl = hook('msg-body');
    if (titleEl) titleEl.textContent = title || 'Shirt Builder';
    if (bodyEl) bodyEl.textContent = body || '';
    openDialog('ccs-msg-ov');
  }

  // ── The style, as the builder wants it ────────────────────────────────────

  /*
   * One color of the SIB style, in the shape openBuilder expects.
   *
   * `allowedPlacements` is derived from which garment shots exist rather than
   * hard-coded: the color feed already drops any color missing either, but a
   * side without a photo has nothing to draw on and must not be offered.
   */
  function shirtFor(colorKey) {
    var color = state.colorsByKey[colorKey];
    return {
      key: style.handle + ':' + colorKey,
      name: style.title,
      color: color.name,
      hex: String(color.hex || '').replace(/^#/, ''),
      img: color.img,
      designFront: color.designFront,
      designBack: color.designBack,
      allowedPlacements: [
        color.designFront ? 'Front' : null,
        color.designBack ? 'Back' : null
      ].filter(Boolean),
      // The builder's own color switcher, which would swap the garment out
      // from under the design. On CCS a color is a product, added through the
      // rail, so there is nothing for it to offer.
      colors: []
    };
  }

  /*
   * A color's stored scenes, in the shape applyRestore expects.
   *
   * Only sides that actually hold a scene are included. applyRestore rejects a
   * view it cannot find a canvas for, and a color that has only ever been
   * drawn on the front legitimately has nothing for the back.
   */
  function documentFor(colorKey) {
    var design = state.designs[colorKey];
    var views = [];
    SIDES.forEach(function (side) {
      if (!design[side].scene) return;
      views.push({ id: side, notes: design[side].notes || '', canvas: design[side].scene });
    });
    return views.length ? { version: 1, views: views } : null;
  }

  /*
   * Put a color's garment and stored scene on the canvas.
   *
   * Wrapped rather than called directly because openBuilder has early returns
   * -- no bridge, no shirt, no usable garment shot -- that hand back undefined
   * instead of a promise. Every caller here chains off the result, and a
   * `.then` on undefined would turn a missing garment into a TypeError.
   */
  function showColor(colorKey) {
    return Promise.resolve(window.CCSBuilder.open({
      shirt: shirtFor(colorKey),
      assignments: [],
      document: documentFor(colorKey)
    }));
  }

  // Read the canvas back out of the builder and store it against whichever
  // color is on screen. Called before anything that moves off that color.
  function snapshot() {
    if (!state || !window.CCSBuilder) return;
    var colorKey = state.active.colorKey;
    var doc = window.CCSBuilder.document();
    var byId = {};
    (doc.views || []).forEach(function (view) { byId[view.id] = view; });
    SIDES.forEach(function (side) {
      var view = byId[side];
      if (!view) return;
      var objects = (view.canvas && view.canvas.objects) || [];
      var design = state.designs[colorKey];
      var previous = design[side].scene;
      var next = objects.length ? view.canvas : null;
      // setScene marks the side dirty, which is what schedules its re-export
      // at publish time. Comparing before writing means simply LOOKING at a
      // color does not invalidate renders that are still good.
      if (JSON.stringify(previous || null) === JSON.stringify(next)) return;
      State.setScene(state, colorKey, side, next);
      design[side].notes = view.notes || '';
    });
  }

  /*
   * Take the canvas as the new baseline WITHOUT calling it an edit.
   *
   * Putting a color on screen round-trips its scene through fabric, which
   * serializes it with every default filled in -- so the JSON that comes back
   * is never byte-identical to the JSON that went in, and snapshot() reads
   * that as the seller having changed something. On an edit that meant every
   * color the seller so much as LOOKED at was re-rendered and re-uploaded on
   * save, replacing files that were already correct.
   *
   * So: snapshot as usual, then put back the rendered URLs of any side that
   * was clean before the swap. Re-serializing is not editing. A side that was
   * already dirty stays dirty, which is what keeps a duplicate exporting
   * everything -- its files have to be its own.
   */
  function rebaseline(colorKey) {
    var design = state.designs[colorKey];
    if (!design) return;

    var before = {};
    SIDES.forEach(function (side) {
      before[side] = {
        dirty: design[side].dirty,
        art: design[side].art,
        preview: design[side].preview,
        sceneUrl: design[side].sceneUrl
      };
    });

    snapshot();

    SIDES.forEach(function (side) {
      if (before[side].dirty) return;
      State.setRendered(state, colorKey, side, before[side]);
    });
  }

  // -- Recent images ---------------------------------------------------------
  //
  // Every image the seller has put on a shirt, kept against their Shopify
  // customer id so it is still there tomorrow. The builder asks for this
  // SYNCHRONOUSLY -- getRecentDesigns() returns an array, not a promise -- so
  // the list is held here and the server is only consulted when it changes.

  var recentImages = [];

  function adoptRecent(data) {
    recentImages = (data && data.images) || [];
    if (window.CCSBuilder && window.CCSBuilder.refreshRecent) {
      window.CCSBuilder.refreshRecent();
    }
  }

  function loadRecent() {
    return Api.getRecentImages().then(adoptRecent).catch(function (error) {
      // A missing history is a missing shortcut, not a broken builder.
      if (window.console && console.warn) console.warn('[ccs] recent images', error);
    });
  }

  /*
   * Remember an image, or move one already remembered to the front.
   *
   * Called for anything that reaches the canvas: an upload, an AI pick, or
   * reusing something already in the list. The server treats a repeat as a
   * touch rather than a second row, so this is safe to call every time.
   *
   * The local copy is updated first so the grid reorders immediately, then
   * corrected by whatever the server says the list now is.
   */
  function remember(url, label, source) {
    if (!url || !/^https:\/\//i.test(url)) return;

    recentImages = [{ img: url, label: label || '', source: source || 'upload' }]
      .concat(recentImages.filter(function (item) { return item.img !== url; }));
    if (window.CCSBuilder && window.CCSBuilder.refreshRecent) {
      window.CCSBuilder.refreshRecent();
    }

    Api.rememberRecentImage({ url: url, label: label, source: source })
      .then(adoptRecent)
      .catch(function () { /* the grid is already right; the next load fixes the order */ });
  }

  function forget(url) {
    if (!url) return;
    recentImages = recentImages.filter(function (item) { return item.img !== url; });
    if (window.CCSBuilder && window.CCSBuilder.refreshRecent) {
      window.CCSBuilder.refreshRecent();
    }
    Api.forgetRecentImage(url).then(adoptRecent).catch(function (error) {
      message('Recent Images', (error && error.message) || 'That could not be removed.');
      loadRecent();
    });
  }

  // ── The color rail ───────────────────────────────────────────────────────

  function swatchBackground(color) {
    var primary = color.hex ? '#' + String(color.hex).replace(/^#/, '') : '#cccccc';
    if (!color.hex2) return primary;
    var secondary = '#' + String(color.hex2).replace(/^#/, '');
    return 'linear-gradient(135deg, ' + primary + ' 0 50%, ' + secondary + ' 50% 100%)';
  }

  function renderColors() {
    var host = hook('colors');
    var template = document.getElementById('ccs-color-row');
    if (!host || !template) return;

    host.textContent = '';
    state.order.forEach(function (colorKey) {
      var color = state.colorsByKey[colorKey];
      var design = state.designs[colorKey];
      var node = template.content.firstElementChild.cloneNode(true);
      var drawn = State.hasArtwork(design.front) || State.hasArtwork(design.back);

      node.dataset.color = colorKey;
      node.classList.toggle('is-active', colorKey === state.active.colorKey);
      node.classList.toggle('is-empty', !drawn);
      node.classList.toggle('is-only', state.order.length === 1);

      hook('sw', node).style.background = swatchBackground(color);
      hook('name', node).textContent = color.name;
      hook('state', node).textContent = drawn ? sizeRange(design.sizes) : 'No design yet';

      // A star on the row, so which color the product opens on is readable
      // without selecting each one to check the box.
      if (colorKey === State.defaultColor(state)) {
        node.classList.add('is-default');
        var star = document.createElement('span');
        star.className = 'ccs-color-star';
        star.title = 'Your product page opens on this color';
        star.setAttribute('aria-label', 'Default color');
        star.textContent = '★';
        hook('name', node).parentNode.appendChild(star);
      }

      host.appendChild(node);
    });

    var count = hook('color-count');
    if (count) {
      count.textContent = state.order.length + ' of ' + State.MAX_COLORS;
    }
    var add = hook('add-color');
    if (add) add.disabled = state.order.length >= State.MAX_COLORS;

    // Only worth offering once there is a design to copy and somewhere to
    // copy it to. Hidden rather than disabled: before either is true it is
    // not a thing the seller is being stopped from doing, it is a thing that
    // has no meaning yet.
    /*
     * The checkbox belongs to the selected color. It is disabled once ticked
     * because there is nothing to untick TO -- a product has to open on some
     * color, so the way to change the default is to tick it somewhere else.
     */
    var defaultRow = hook('default-row');
    if (defaultRow) {
      var isDefault = state.active.colorKey === State.defaultColor(state);
      defaultRow.hidden = state.order.length < 2;
      defaultRow.classList.toggle('is-on', isDefault);
      var box = hook('set-default');
      if (box) {
        box.checked = isDefault;
        box.disabled = isDefault;
        box.title = isDefault
          ? 'Your product page already opens on this color'
          : 'Open the product page on this color';
      }
    }

    var cycle = hook('cycle');
    if (cycle) {
      cycle.hidden = state.order.length < 2;
      var index = state.order.indexOf(state.active.colorKey);
      var at = hook('cycle-at');
      if (at) at.textContent = (index + 1) + ' of ' + state.order.length;

      // Disabled at the ends, because these do not wrap: a dead button is how
      // the strip says "this is the first one" without being told.
      var prev = hook('color-prev');
      var next = hook('color-next');
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index < 0 || index >= state.order.length - 1;
    }

    var apply = hook('apply-design');
    if (apply) {
      var source = state.designs[state.active.colorKey];
      apply.hidden = state.order.length < 2 ||
        !(State.hasArtwork(source.front) || State.hasArtwork(source.back));
    }
  }

  /*
   * "S – 5XL", or the one size if that is all there is.
   *
   * Sizes are not chosen: a listing offers every size the color is stocked
   * in. The run genuinely differs by color -- the same style can come in
   * S-5XL in black and S-3XL in an odd heather -- so the range is worth
   * stating per row even though there is nothing to decide.
   */
  function sizeRange(sizes) {
    var list = sizes || [];
    if (!list.length) return 'No sizes available';
    if (list.length === 1) return list[0];
    return list[0] + ' – ' + list[list.length - 1];
  }

  function renderRail() {
    renderColors();
    makeColorsSortable();
    renderFeature();
  }

  // -- The feature photo -----------------------------------------------------
  //
  // Borrows Add Images, so the seller gets recent artwork, an upload and AI
  // generation without a second picker being built. The builder routes the
  // result here instead of onto the canvas -- see pickImage in
  // rederive-ccs.js.

  function renderFeature() {
    var shown = hook('feature');
    var add = hook('feature-pick');
    if (!shown) return;

    var url = state && state.featureImageUrl;
    shown.hidden = !url;
    if (url) {
      var img = hook('feature-img');
      if (img) img.src = url;
    }
    // The add button and the preview are the same slot: one or the other.
    // Replace lives on the preview, so the button is only for the empty state.
    if (add) add.hidden = !!url;
  }

  function pickFeature() {
    if (!window.CCSBuilder || !window.CCSBuilder.pickImage) return;
    window.CCSBuilder.pickImage(function (url) {
      /*
       * Whatever Add Images produced is already on S3 -- uploads go through
       * uploadArtwork and AI picks are registered the same way -- so there is
       * nothing to upload here, only a URL to remember.
       */
      State.setFeatureImage(state, url);
      renderFeature();
    });
  }

  function clearFeature() {
    State.setFeatureImage(state, null);
    renderFeature();
  }

  // -- Reordering the colors -------------------------------------------------
  //
  // The order in the rail is the order shoppers see: it becomes the Color
  // option's value order on the published product. It used to be fixed at
  // whichever order the seller happened to add them in, which is the order
  // they were thinking about colors, not the order they want to sell them.

  function commitColorOrder(keys) {
    State.setColorOrder(state, keys);
    renderRail();
  }

  /*
   * Drag to reorder, with HTML5 drag and drop.
   *
   * Bound once to the list rather than per row, because the rows are rebuilt
   * on every render and per-row listeners would leak with them.
   *
   * Only the grip starts a drag. Making the whole row draggable would mean a
   * click that moves two pixels becomes a drag instead of selecting a color,
   * which is the commonest thing a seller does here.
   */
  function makeColorsSortable() {
    var list = hook('colors');
    if (!list || list.dataset.sortable === 'on') return;
    list.dataset.sortable = 'on';

    var dragging = null;

    list.addEventListener('mousedown', function (event) {
      var grip = event.target.closest('[data-ccs="color-grip"]');
      var row = grip && grip.closest('.ccs-color');
      // Set just before the drag can begin, and cleared after, so a row is
      // never draggable while the seller is only trying to click it.
      if (row) row.draggable = true;
    });

    list.addEventListener('dragstart', function (event) {
      var row = event.target.closest('.ccs-color');
      if (!row || !row.draggable) return;
      dragging = row;
      row.classList.add('is-dragging');
      // Firefox refuses to start a drag with nothing on the transfer.
      try { event.dataTransfer.setData('text/plain', row.dataset.color || ' '); } catch (e) {}
      event.dataTransfer.effectAllowed = 'move';
    });

    list.addEventListener('dragover', function (event) {
      if (!dragging) return;
      event.preventDefault();
      var over = event.target.closest('.ccs-color');
      if (!over || over === dragging) return;
      var box = over.getBoundingClientRect();
      // Past the midpoint means after it.
      var after = event.clientY > box.top + box.height / 2;
      over.parentNode.insertBefore(dragging, after ? over.nextSibling : over);
    });

    list.addEventListener('drop', function (event) { event.preventDefault(); });

    list.addEventListener('dragend', function () {
      if (!dragging) return;
      dragging.classList.remove('is-dragging');
      dragging.draggable = false;
      dragging = null;
      commitColorOrder(Array.prototype.slice.call(list.querySelectorAll('.ccs-color'))
        .map(function (row) { return row.dataset.color; }));
    });

    /*
     * The same move from the keyboard. Focus stays on the grip of the row that
     * moved, so a seller can walk a color up several places without hunting
     * for it again after each step.
     *
     * stopPropagation because the builder nudges a selected element with the
     * arrows; with a grip focused the keys belong to the list.
     */
    list.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      var grip = event.target.closest('[data-ccs="color-grip"]');
      var row = grip && grip.closest('.ccs-color');
      if (!row) return;

      event.preventDefault();
      event.stopPropagation();
      var colorKey = row.dataset.color;
      State.moveColor(state, colorKey, event.key === 'ArrowDown' ? 1 : -1);
      renderRail();

      var moved = list.querySelector('.ccs-color[data-color="' + colorKey + '"]');
      var handle = moved && hook('color-grip', moved);
      if (handle) handle.focus();
    });
  }

  // ── Switching color ──────────────────────────────────────────────────────

  /*
   * Put a different color on the canvas.
   *
   * The snapshot has to happen before openBuilder disposes the canvases, and
   * the re-open has to finish before the seller can touch anything, hence the
   * guard — two of these overlapping would leave the rail pointing at one
   * color and the canvas holding another.
   */
  function selectColor(colorKey) {
    if (switching || colorKey === state.active.colorKey) return Promise.resolve();
    if (!state.designs[colorKey]) return Promise.resolve();

    snapshot();
    switching = true;
    /*
     * Keep the side. Looking at purple's back and clicking red should show
     * red's back -- openBuilder always lands on the front, so it is restored
     * once the swap has landed.
     *
     * Done here rather than in the callers, so every route to another color
     * behaves the same: the rail, Prev/Next, the arrow keys, and whatever
     * lands here after a removal.
     */
    var side = window.CCSBuilder && window.CCSBuilder.side && window.CCSBuilder.side();
    state.active.colorKey = colorKey;
    renderRail();

    return showColor(colorKey).then(function () {
      rebaseline(colorKey);
      if (side && side !== 'front' && window.CCSBuilder.showSide) {
        window.CCSBuilder.showSide(side);
      }
    }).catch(function (error) {
      message('Shirt Builder', (error && error.message) || 'That color could not be opened.');
    }).then(function () {
      switching = false;
      renderRail();
    });
  }

  /*
   * Picking colors is one decision, made once.
   *
   * A seller choosing a range wants black, white and navy -- not three trips
   * through the same dialog, each one closing and reopening the canvas
   * underneath. So the grid toggles and a single submit adds the lot.
   *
   * Colors already in the listing are not offered. Adding what you already
   * have is not a thing anyone is trying to do, and showing them would make
   * the grid longer for no reason.
   */
  function colorPickerSlots() {
    return State.MAX_COLORS - state.order.length;
  }

  function pickerSwatches() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-ccs="choose-color"]'));
  }

  function pickerSelection() {
    return pickerSwatches()
      .filter(function (button) { return button.classList.contains('is-on'); })
      .map(function (button) { return button.dataset.color; });
  }

  function refreshPicker() {
    var swatches = pickerSwatches();
    var chosen = pickerSelection();
    var slots = colorPickerSlots();
    // The most that can be taken in this pass: whichever runs out first, the
    // colors on offer or the room left in the listing.
    var selectable = Math.min(swatches.length, slots);

    /*
     * At the cap, the unpicked ones stop responding rather than letting the
     * seller choose twelve and be told on submit that four of them did not
     * fit. The limit is a fact about the listing, so it belongs where the
     * choosing happens.
     */
    swatches.forEach(function (button) {
      var on = button.classList.contains('is-on');
      button.disabled = !on && chosen.length >= slots;
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var count = hook('color-count-sel');
    if (count) {
      count.textContent = chosen.length
        ? chosen.length + ' selected, room for ' + (slots - chosen.length) + ' more'
        : 'You can add up to ' + slots + ' more color' + (slots === 1 ? '' : 's');
    }

    var all = hook('color-toggle-all');
    // The link says what pressing it does, not what the state is.
    if (all) {
      all.hidden = !selectable;
      all.textContent = chosen.length >= selectable ? 'Select none' : 'Select all';
    }

    var number = hook('color-n');
    if (number) number.textContent = String(chosen.length);
    var submit = hook('color-submit');
    if (submit) submit.disabled = chosen.length === 0;
  }

  function togglePickerColor(button) {
    if (button.disabled) return;
    button.classList.toggle('is-on');
    refreshPicker();
  }

  function togglePickerAll() {
    var swatches = pickerSwatches();
    var slots = colorPickerSlots();
    var turnOn = pickerSelection().length < Math.min(swatches.length, slots);
    swatches.forEach(function (button, index) {
      button.classList.toggle('is-on', turnOn && index < slots);
    });
    refreshPicker();
  }

  /*
   * Step to the next or previous color on the listing.
   *
   * Wraps, and the buttons never disable. The point of this is sweeping
   * through every color looking for artwork that sits wrong on one of them --
   * going dead at the last one would stop that sweep halfway round, which is
   * the moment it is least welcome.
   *
   * selectColor refuses while a swap is still loading, so leaning on the
   * button queues nothing: each press either moves or is ignored.
   */
  /*
   * Open the product page on the color being looked at.
   *
   * Nothing is published by this -- it rides along with the listing when the
   * seller publishes, the same as the artwork does.
   */
  function makeDefaultColor() {
    var result = State.setDefaultColor(state, state.active.colorKey);
    if (result.error) { message('Colors', result.error); return; }
    renderRail();
  }

  function cycleColor(step) {
    var at = state.order.indexOf(state.active.colorKey);
    if (at === -1) at = 0;
    var to = at + step;
    // Stops at the ends rather than wrapping; the buttons disable there.
    if (to < 0 || to >= state.order.length) return;

    // The side comes with it, handled inside selectColor so every route to
    // another color behaves the same.
    selectColor(state.order[to]);
  }

  function openColorPicker() {
    var grid = hook('color-grid');
    if (!grid) return;
    showError(hook('color-error'), '');
    grid.textContent = '';

    var remaining = style.colors.filter(function (color) { return !state.designs[color.key]; });
    if (!remaining.length) {
      showError(hook('color-error'), 'Every color this garment comes in is already in your design.');
    }
    if (colorPickerSlots() <= 0) {
      message('Colors', 'You can offer up to ' + State.MAX_COLORS +
        ' colors on one design. Remove one to add another.');
      return;
    }

    remaining.forEach(function (color) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'ccs-swatch';
      button.dataset.ccs = 'choose-color';
      button.dataset.color = color.key;
      button.setAttribute('aria-pressed', 'false');

      var img = document.createElement('img');
      img.src = color.img;
      img.alt = '';
      img.loading = 'lazy';
      var name = document.createElement('span');
      name.textContent = color.name;
      var tick = document.createElement('span');
      tick.className = 'ccs-swatch-tick';
      tick.setAttribute('aria-hidden', 'true');
      tick.innerHTML = '&#10003;';

      button.appendChild(img);
      button.appendChild(name);
      button.appendChild(tick);
      grid.appendChild(button);
    });

    refreshPicker();
    openDialog('ccs-color-ov');
  }

  /*
   * Add everything selected, in the order the style lists them.
   *
   * Snapshot first: addColor forks the design currently on screen, and the
   * version on screen is newer than the version in state until that runs.
   * `active` does not move during the loop, so every new color forks the same
   * design rather than a chain of copies of copies.
   *
   * The canvas is deliberately left where it is. Switching to one of six
   * colors just added would rebuild the stage for no reason and take the
   * seller off the design they were working on; the rail shows the new rows,
   * which is the confirmation that was needed.
   */
  function addColors() {
    var chosen = pickerSelection();
    if (!chosen.length) return;
    snapshot();

    var failed = null;
    style.colors.forEach(function (color) {
      if (chosen.indexOf(color.key) === -1) return;
      var result = State.addColor(state, color.key);
      if (result.error && !failed) failed = result.error;
    });

    closeDialog('ccs-color-ov');
    renderRail();
    if (failed) message('Colors', failed);
  }

  function removeColor(colorKey) {
    if (switching) return;
    var wasActive = colorKey === state.active.colorKey;
    // Only if it survives: snapshot writes to the color on screen, and
    // writing to one that is about to be deleted is work thrown away.
    if (!wasActive) snapshot();

    var result = State.removeColor(state, colorKey);
    if (result.error) { message('Colors', result.error); return; }
    renderRail();
    if (!wasActive) return;

    // The canvas is still showing the color that no longer exists.
    // removeColor has already chosen where `active` lands, so this just puts
    // that color on screen.
    switching = true;
    var landedOn = state.active.colorKey;
    showColor(landedOn).then(function () {
      rebaseline(landedOn);
    }).catch(function (error) {
      message('Shirt Builder', (error && error.message) || 'That color could not be opened.');
    }).then(function () {
      switching = false;
      renderRail();
    });
  }

  // -- Applying one design to other colors -----------------------------------
  //
  // The problem this solves: a seller picks six colors, builds the design,
  // then wants to move the artwork half an inch. Without this, that nudge has
  // to be repeated on every color, by hand, identically -- and the first one
  // they get slightly wrong is the one a shopper sees.
  //
  // Which colors get overwritten is ASKED, never assumed. Artwork inverted for
  // a dark garment is a deliberate difference, and quietly flattening it with
  // the light version would destroy work the seller cannot get back.

  /*
   * A garment photo's natural size, cached onto the color.
   *
   * Two things need it: the preview canvases below, which have to match the
   * garment's aspect ratio, and State.rebaseScene, which uses it to keep
   * artwork at the same HEIGHT ON THE SHIRT when it moves between colors shot
   * at different ratios. rebaseScene is a no-op without it, so measuring here
   * is what makes the copy land correctly as well as look right.
   */
  function measure(url) {
    return new Promise(function (resolve) {
      if (!url) { resolve(null); return; }
      var img = new Image();
      img.onload = function () {
        resolve({ w: img.naturalWidth || 1000, h: img.naturalHeight || 1200 });
      };
      img.onerror = function () { resolve(null); };
      img.src = url;
    });
  }

  function measureColor(colorKey) {
    var color = state.colorsByKey[colorKey];
    if (color.natural) return Promise.resolve(color.natural);
    return measure(color.designFront || color.designBack).then(function (natural) {
      if (natural) color.natural = natural;
      return natural;
    });
  }

  /*
   * Draw one color's stored scene onto a canvas, over its garment photo.
   *
   * A live canvas rather than a data URL on purpose. Exporting would mean
   * toDataURL on a canvas holding cross-origin artwork, which taints it and
   * throws -- the builder works around that with blob URLs it has already
   * fetched, and none of that machinery is reachable from here. Showing the
   * canvas itself has no such problem.
   *
   * The canvas sits at inset:0 over the garment, which is exactly the geometry
   * the builder uses, so no positioning has to be recomputed: the scene's
   * coordinates already mean what they mean.
   */
  function paintPreview(slot, colorKey, side) {
    var design = state.designs[colorKey];
    var stored = design[side] && design[side].scene;
    var color = state.colorsByKey[colorKey];
    var garment = side === 'front' ? color.designFront : color.designBack;

    if (!garment) {
      slot.hidden = true;
      return Promise.resolve();
    }

    /*
     * The garment is drawn whether or not this side carries artwork.
     *
     * A blank side IS the answer to what the seller is being asked -- "what
     * are you about to replace" -- and drawing it keeps every card the same
     * shape, so ticking Front alone cannot leave a color whose only design is
     * on the back showing an empty frame.
     */
    slot.dataset.drawn = '1';
    slot.hidden = !sideShown(side);

    var shot = document.createElement('img');
    shot.className = 'ccs-apply-garment';
    shot.src = garment;
    shot.alt = '';
    slot.appendChild(shot);

    return measureColor(colorKey).then(function (natural) {
      // The garment is up; a side with nothing on it is finished here.
      if (!stored || !(stored.objects || []).length || !window.fabric) {
        if (natural) slot.style.aspectRatio = natural.w + ' / ' + natural.h;
        return null;
      }

      var width = 1000;
      var height = natural ? Math.round(width * natural.h / natural.w) : 1250;
      var element = document.createElement('canvas');
      element.className = 'ccs-apply-art';
      slot.appendChild(element);

      // The slot takes the garment's own proportions, so the photo (which is
      // object-fit: contain) fills it exactly and the canvas laid over it
      // lines up with the shirt rather than with a letterboxed box.
      if (natural) slot.style.aspectRatio = natural.w + ' / ' + natural.h;

      var canvas = new window.fabric.StaticCanvas(element, {
        width: width, height: height, enableRetinaScaling: false
      });
      return new Promise(function (resolve) {
        // Cloned before loading: fabric writes back into the object it is
        // given, and this scene is the seller's live design.
        var scene = JSON.parse(JSON.stringify(stored));
        (scene.objects || []).forEach(function (object) { object.crossOrigin = 'anonymous'; });
        canvas.loadFromJSON(scene, function () {
          canvas.renderAll();
          /*
           * Fabric writes the canvas size onto the element as an INLINE style,
           * which beats the stylesheet -- so the preview drew at its full
           * 1000px inside a 150px slot and all anyone saw was the empty
           * top-left corner of it. cssOnly keeps the drawing surface at design
           * resolution and scales only what is displayed.
           */
          canvas.setDimensions({ width: '100%', height: '100%' }, { cssOnly: true });
          resolve();
        });
      });
    }).catch(function () { /* a preview that will not draw is not worth failing over */ });
  }

  // Which colors the dialog is offering, and which of those are ticked.
  var applyTargets = [];

  /*
   * Which SIDES the apply will replace.
   *
   * The dialog asks two questions -- which colors, and which sides -- and
   * both of them narrow the same operation. The second exists because the
   * commonest edit is to one side: the backs across every color are already
   * right and the front needs moving, and replacing both would overwrite
   * thirteen correct backs to fix one front.
   */
  function applySides() {
    return hooks('apply-side')
      .filter(function (box) { return box.checked; })
      .map(function (box) { return box.value; });
  }

  function sideShown(side) {
    return applySides().indexOf(side) !== -1;
  }

  /*
   * Show each card the sides it is about to lose, and nothing else.
   *
   * The previews are not repainted -- drawing thirteen fabric canvases again
   * on every tick would be slow and pointless. They are already there; this
   * only decides which are visible.
   */
  function applySideVisibility() {
    var sides = applySides();

    SIDES.forEach(function (side) {
      hooks('apply-' + side).forEach(function (slot) {
        slot.hidden = !(slot.dataset.drawn === '1' && sides.indexOf(side) !== -1);
      });
    });

    // With no side ticked there is no decision left to make, so the colors go
    // away rather than standing there as empty frames.
    var grid = hook('apply-grid');
    var empty = hook('apply-empty');
    var bar = document.querySelector('.ccs-apply-bar');
    if (grid) grid.hidden = !sides.length;
    if (bar) bar.hidden = !sides.length;
    if (empty) empty.hidden = !!sides.length;
  }

  function applySelection() {
    return Array.prototype.slice
      .call(document.querySelectorAll('[data-ccs="apply-toggle"].is-on'))
      .map(function (card) { return card.dataset.color; });
  }

  function refreshApplyCount() {
    var chosen = applySelection().length;
    var count = hook('apply-count');
    var number = hook('apply-n');
    var all = hook('apply-toggle-all');
    var submit = hook('apply-submit');
    if (count) {
      count.textContent = chosen + ' of ' + applyTargets.length + ' selected';
    }
    if (number) number.textContent = String(chosen);
    // The link says what pressing it does, not what the state is.
    if (all) all.textContent = chosen === applyTargets.length ? 'Select none' : 'Select all';
    // Both questions have to be answered: a color with no side, or a side
    // with no color, would apply nothing.
    if (submit) submit.disabled = chosen === 0 || applySides().length === 0;
  }

  function toggleApply(card) {
    card.classList.toggle('is-on');
    card.setAttribute('aria-pressed', card.classList.contains('is-on') ? 'true' : 'false');
    refreshApplyCount();
  }

  function toggleApplyAll() {
    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-ccs="apply-toggle"]'));
    var turnOn = applySelection().length !== applyTargets.length;
    cards.forEach(function (card) {
      card.classList.toggle('is-on', turnOn);
      card.setAttribute('aria-pressed', turnOn ? 'true' : 'false');
    });
    refreshApplyCount();
  }

  function openApply() {
    snapshot();
    var sourceKey = state.active.colorKey;
    var source = state.designs[sourceKey];
    if (!State.hasArtwork(source.front) && !State.hasArtwork(source.back)) {
      message('Apply Design', 'There is nothing on this color to copy yet.');
      return;
    }

    // Only colors that already carry a design. A color with nothing on it has
    // no work to lose, so there is no decision to put in front of the seller
    // -- it is filled in on the way past.
    applyTargets = state.order.filter(function (colorKey) {
      if (colorKey === sourceKey) return false;
      var design = state.designs[colorKey];
      return State.hasArtwork(design.front) || State.hasArtwork(design.back);
    });

    // A color with nothing on it is left alone. addColor forks the design on
    // screen, so a color is only ever blank because the seller cleared it --
    // which is a decision, usually "I am going to draw something different
    // here". Filling it in would quietly undo that. It is named below so its
    // absence from the grid does not read as a bug.
    var blank = state.order.filter(function (colorKey) {
      if (colorKey === sourceKey) return false;
      var design = state.designs[colorKey];
      return !State.hasArtwork(design.front) && !State.hasArtwork(design.back);
    });

    var sourceName = hook('apply-source');
    if (sourceName) sourceName.textContent = state.colorsByKey[sourceKey].name;

    /*
     * Both sides on, every time the dialog opens -- replacing the whole
     * design is still the commoner intent, and a choice that persisted from
     * the last time would silently skip a side.
     *
     * Except a side the source has nothing on. Applying that can only clear
     * the target, so it starts off and says why: a seller who does want to
     * wipe every back can still tick it.
     */
    var missing = [];
    hooks('apply-side').forEach(function (box) {
      var has = State.hasArtwork(source[box.value]);
      box.checked = has;
      if (!has) missing.push(box.value);
    });

    var sideNote = hook('apply-side-note');
    if (sideNote) {
      sideNote.hidden = !missing.length;
      sideNote.textContent = missing.length
        ? state.colorsByKey[sourceKey].name + ' has no ' + missing.join(' or ') +
          ' design — applying ' + (missing.length === 1 ? 'it' : 'them') + ' would clear it.'
        : '';
    }

    var blankNote = hook('apply-blank');
    if (blankNote) {
      blankNote.hidden = !blank.length;
      blankNote.textContent = blank.length
        ? blank.length + ' color' + (blank.length === 1 ? '' : 's') +
          ' with no design yet ' + (blank.length === 1 ? 'is' : 'are') +
          ' not shown. Open one to design it.'
        : '';
    }

    var grid = hook('apply-grid');
    var template = document.getElementById('ccs-apply-card');
    if (!grid || !template) return;
    grid.textContent = '';

    applyTargets.forEach(function (colorKey) {
      var node = template.content.firstElementChild.cloneNode(true);
      node.dataset.color = colorKey;
      // Everything on by default: copying to all of them is the common case,
      // and deselecting the odd inverted one is the exception.
      node.classList.add('is-on');
      node.setAttribute('aria-pressed', 'true');
      hook('apply-name', node).textContent = state.colorsByKey[colorKey].name;
      grid.appendChild(node);
      paintPreview(hook('apply-front', node), colorKey, 'front');
      paintPreview(hook('apply-back', node), colorKey, 'back');
    });

    if (!applyTargets.length) {
      message('Apply Design', blank.length
        ? 'The other colors have no design on them yet. Open one to design it.'
        : 'There are no other colors to apply this design to.');
      return;
    }

    showError(hook('apply-error'), '');
    applySideVisibility();
    refreshApplyCount();
    openDialog('ccs-apply-ov');
  }

  /*
   * Copy the design on screen onto every selected color.
   *
   * Only the SIDES that are ticked. A side left unticked is not touched at
   * all, which is the point of asking -- the backs stay exactly as they are
   * while the front is corrected everywhere.
   *
   * Within a ticked side, an empty source still writes: "replace" has to mean
   * replace there, or a color keeps a design the source does not have and the
   * seller has no way to see it until the product is live.
   *
   * Each copy is rebased for the target's aspect ratio, the same way adding a
   * color forks the current design -- so artwork sits at the same point on the
   * shirt rather than at the same numeric coordinate.
   */
  function applyDesign() {
    var chosen = applySelection();
    var sides = applySides();
    var sourceKey = state.active.colorKey;
    var source = state.designs[sourceKey];
    if (!chosen.length || !sides.length) { closeDialog('ccs-apply-ov'); return; }

    Promise.all([measureColor(sourceKey)].concat(chosen.map(measureColor))).then(function () {
      chosen.forEach(function (colorKey) {
        sides.forEach(function (side) {
          var scene = source[side] && source[side].scene;
          if (!scene) { State.setScene(state, colorKey, side, null); return; }
          State.setScene(state, colorKey, side, State.rebaseScene(
            JSON.parse(JSON.stringify(scene)),
            state.colorsByKey[sourceKey],
            state.colorsByKey[colorKey]
          ));
        });
      });
      closeDialog('ccs-apply-ov');
      renderRail();
      // Which sides, because "applied to 13 colors" reads as the whole design
      // and the seller may have deliberately spared one.
      message('Apply Design',
        'Applied the ' + sides.join(' and ') + ' to ' +
        chosen.length + ' color' + (chosen.length === 1 ? '' : 's') + '.');
    }).catch(function (error) {
      showError(hook('apply-error'),
        (error && error.message) || 'That could not be applied. Please try again.');
    });
  }

  // ── Pricing ───────────────────────────────────────────────────────────────

  function markupDollars() {
    var slider = hook('markup');
    return slider ? Number(slider.value) : 3;
  }

  /*
   * Ask the server what shoppers would pay.
   *
   * Quoted rather than computed here because the blank cost changes daily and
   * only the server knows today's — and because a price the browser worked out
   * is a price the browser could have worked out differently.
   */
  function refreshQuote() {
    var value = hook('markup-value');
    var range = hook('quote-range');
    if (value) value.textContent = money(markupDollars());
    if (!range) return;

    state.markupCents = Math.round(markupDollars() * 100);
    var token = ++quoteToken;
    range.textContent = '…';

    Api.quote(State.toQuotePayload(state)).then(function (data) {
      if (token !== quoteToken) return;   // a newer drag has overtaken this one
      range.textContent = data.minPrice === data.maxPrice
        ? money(data.minPrice)
        : money(data.minPrice) + ' – ' + money(data.maxPrice);
    }).catch(function () {
      if (token !== quoteToken) return;
      range.textContent = '—';
    });
  }

  // ── Publishing ────────────────────────────────────────────────────────────

  function openPublish() {
    snapshot();
    var check = State.validate(state);
    if (!check.valid) { message('Publish', check.errors[0]); return; }
    showError(hook('publish-error'), '');
    openDialog('ccs-publish-ov');
    refreshQuote();
  }

  /*
   * Export and upload every color that has work the server has not seen.
   *
   * Runs one color at a time, on purpose: each pass puts that color's
   * garment and scene on the single canvas the builder owns, so they cannot
   * overlap. Colors whose renders are still current are skipped, which is
   * what makes re-publishing after a small edit quick.
   */
  function renderAllColors() {
    var pending = {};
    State.sidesNeedingExport(state).forEach(function (entry) { pending[entry.colorKey] = true; });
    var queue = state.order.filter(function (colorKey) { return pending[colorKey]; });
    if (!queue.length) return Promise.resolve();

    var chain = Promise.resolve();
    queue.forEach(function (colorKey, index) {
      chain = chain.then(function () {
        window.CCSBuilder.busy(true,
          'Preparing ' + state.colorsByKey[colorKey].name +
          ' (' + (index + 1) + ' of ' + queue.length + ')…');

        var already = colorKey === state.active.colorKey;
        state.active.colorKey = colorKey;
        var ready = already ? Promise.resolve() : showColor(colorKey);

        return ready.then(function () {
          // prepare() shows no progress of its own — the caller owns the
          // overlay — so the label above stays up for the whole color.
          window.CCSBuilder.busy(true,
            'Preparing ' + state.colorsByKey[colorKey].name +
            ' (' + (index + 1) + ' of ' + queue.length + ')…');
          return window.CCSBuilder.prepare();
        }).then(function (sides) {
          sides.forEach(function (side) {
            State.setRendered(state, colorKey, side.placement, {
              art: side.img,
              preview: side.preview,
              sceneUrl: side.sceneUrl
            });
          });
        });
      });
    });
    return chain;
  }

  function publish() {
    var nameInput = $('#ccs-listing-name');
    var descInput = $('#ccs-listing-desc');
    var error = hook('publish-error');
    var button = hook('publish');

    var name = (nameInput && nameInput.value || '').trim();
    if (!name) {
      showError(error, 'Give this design a name so shoppers know what it is.');
      if (nameInput) nameInput.focus();
      return;
    }

    snapshot();
    var check = State.validate(state);
    if (!check.valid) { showError(error, check.errors[0]); return; }

    showError(error, '');
    if (button) button.disabled = true;
    closeDialog('ccs-publish-ov');
    switching = true;

    renderAllColors().then(function () {
      window.CCSBuilder.busy(true, isEdit ? 'Saving your changes…' : 'Publishing your design…');
      var payload = State.toListingPayload(state, {
        name: name,
        description: (descInput && descInput.value || '').trim()
      });
      // Same payload either way. The only difference is whether it becomes a
      // new listing or writes over the one it came from.
      if (isEdit) {
        payload.uid = copying.uid;
        return Api.updateListingDesign(payload);
      }
      return Api.createListing(payload);
    }).then(function (data) {
      // Straight to the store. The point of publishing is seeing it listed,
      // and the builder would otherwise be covering the thing it just made.
      window.CCSBuilder.busy(true, 'Done. Taking you to your store…');
      var listing = data && data.listing;
      window.location.href = listing && listing.handle
        ? '/products/' + listing.handle
        : '/pages/store';
    }).catch(function (err) {
      switching = false;
      if (button) button.disabled = false;
      window.CCSBuilder.busy(false);
      // Rendering walks the colors, so whichever one it stopped on is the one
      // on screen. Say so, rather than leaving the rail pointing elsewhere.
      renderRail();
      openDialog('ccs-publish-ov');
      showError(error, (err && err.message) || 'That design could not be published. Please try again.');
    });
  }

  // ── The bridge ────────────────────────────────────────────────────────────
  //
  // Same shape as window.CustomDesigns on SIB and window.SBAdminHost in the
  // admin, minus everything with no meaning for a seller publishing a product:
  // sizes (they are in the rail), the cart, saved designs and sign-in. The
  // builder guards every one of those calls, so absence is the way to remove a
  // feature — there is nothing to stub.

  window.CCSBuilderHost = {
    /*
     * Everything the builder uploads comes through here: the seller's artwork,
     * the flattened print file, the fabric scene and the preview. All of it to
     * the same bucket the SIB builder uses, through the presigned POST in
     * assets/ccs-upload.js.
     */
    uploadArtwork: function (file, options) {
      var opts = options || {};
      return Upload.upload(file).then(function (url) {
        var label = file.name || 'Artwork';
        /*
         * addToHistory is false for the builder's own exports -- the print
         * file, the scene, the flattened preview. Those are products of a
         * design, not artwork a seller would reuse, and putting them in the
         * grid would bury the real uploads under renders of shirts.
         */
        if (opts.addToHistory !== false) remember(url, label, opts.source || 'upload');
        return { img: url, label: label };
      });
    },

    // The seller's own pile of artwork, newest first. Synchronous by
    // contract: the builder renders straight from what this returns.
    getRecentDesigns: function () { return recentImages; },

    // An AI pick, registered the moment it lands on the shirt.
    registerArtwork: function (entry) {
      if (entry && entry.url) remember(entry.url, entry.label, entry.source || 'ai');
    },

    // Reusing something moves it back to the front of the list.
    touchArtwork: function (url) { remember(url, null, null); },

    // The x on a card. The file stays on S3 -- a published product still
    // prints from it, so this removes the shortcut, not the artwork.
    forgetRecentDesign: forget,


    // The page is only reachable behind a customer account, and the app proxy
    // rejects an unauthenticated call regardless. Saying "no" here would put
    // the builder into a sign-in flow that has been stripped out of this copy.
    isSignedIn: function () { return signedIn; },

    // SIB uses this to suppress floating chat widgets while the overlay is up.
    // Nothing on this theme floats over it.
    setOverlayMask: function () {},

    showMessage: function (payload) {
      message((payload && payload.title) || 'Shirt Builder', payload && payload.message);
    },

    // The primary action. The builder has already checked there is something
    // on the shirt; everything from here is the host's, because publishing
    // spans every color and the builder only ever holds one.
    publishListing: openPublish,

    // Reached by closing the builder. There is no modal behind it on this
    // page, so closing has to go somewhere, and the dashboard is where the
    // seller's designs and store details are. /pages/sell is the pitch for
    // people who do not have a store yet -- for someone who just left the
    // design studio it had nothing on it at all.
    returnFromBuilder: function () {
      if (switching) return;   // mid-publish teardown, not a seller leaving
      window.location.href = EXIT_URL;
    }
  };

  // ── Wiring ────────────────────────────────────────────────────────────────

  /*
   * The data-ccs values that are ACTIONS, as opposed to slots something is
   * written into.
   *
   * The distinction matters because a click lands on whatever is under the
   * pointer, and closest('[data-ccs]') then finds the nearest hook of ANY
   * kind. A color row is a button carrying the action, with the swatch, the
   * name and the size range inside it carrying slots -- so clicking the name
   * found data-ccs="name", matched nothing, and did nothing. Only the button's
   * own padding worked, which is exactly as broken as it sounds.
   *
   * Listing the actions and looking for those alone fixes the whole class,
   * rather than this one row.
   */
  var ACTIONS = [
    'add-color', 'choose-color', 'pick-color', 'remove-color', 'publish',
    'apply-design', 'apply-toggle', 'apply-toggle-all', 'apply-submit', 'apply-side',
    'color-toggle-all', 'color-submit', 'color-prev', 'color-next', 'set-default',
    'feature-pick', 'feature-clear',
    'close-color', 'close-publish', 'close-msg', 'close-apply'
  ];
  var ACTION_SELECTOR = ACTIONS.map(function (name) {
    return '[data-ccs="' + name + '"]';
  }).join(',');

  document.addEventListener('click', function (event) {
    var target = event.target.closest(ACTION_SELECTOR);
    if (!target) return;
    var action = target.dataset.ccs;

    switch (action) {
      case 'add-color':    openColorPicker(); break;
      case 'choose-color':     togglePickerColor(target); break;
      case 'color-toggle-all': togglePickerAll(); break;
      case 'color-submit':     addColors(); break;
      case 'pick-color':   selectColor(target.closest('.ccs-color').dataset.color); break;
      case 'remove-color': removeColor(target.closest('.ccs-color').dataset.color); break;
      case 'publish':      publish(); break;

      case 'set-default':      makeDefaultColor(); break;

      case 'feature-pick':     pickFeature(); break;
      case 'feature-clear':    clearFeature(); break;

      case 'color-prev':       cycleColor(-1); break;
      case 'color-next':       cycleColor(1); break;

      case 'apply-design':     openApply(); break;
      case 'apply-toggle':     toggleApply(target); break;
      case 'apply-toggle-all': toggleApplyAll(); break;
      // The box has already toggled itself by the time this runs -- the click
      // is on the input, so its checked state is current.
      case 'apply-side':       applySideVisibility(); refreshApplyCount(); break;
      case 'apply-submit':     applyDesign(); break;

      // A backdrop and its close button carry the same hook, so clicking
      // through the box itself must not count.
      case 'close-color':   if (event.target === target || target.tagName === 'BUTTON') closeDialog('ccs-color-ov'); break;
      case 'close-publish': if (event.target === target || target.tagName === 'BUTTON') closeDialog('ccs-publish-ov'); break;
      case 'close-msg':     if (event.target === target || target.tagName === 'BUTTON') closeDialog('ccs-msg-ov'); break;
      case 'close-apply':   if (event.target === target || target.tagName === 'BUTTON') closeDialog('ccs-apply-ov'); break;
      default: break;
    }
  });

  /*
   * Escape closes the CCS dialog on top, and nothing else.
   *
   * The builder has its own Escape handler on `document`, and it works
   * through a list of ITS sub-overlays -- start over, notes, fonts, and the
   * rest. It has never heard of the dialogs this file adds, so pressing
   * Escape over one of them fell all the way through that list to
   * requestExit(), which closed the whole builder. The dialog went away and
   * took the design studio with it, leaving a blank page.
   *
   * Registered in the CAPTURE phase so it runs before the builder's listener
   * on the same node, and stopPropagation keeps the event from ever reaching
   * it. Nothing in the builder needed changing, which matters: it is
   * re-derived, and a fix made here survives the next re-derive.
   *
   * Topmost first. A message raised over the publish dialog is the one
   * Escape should take, not the dialog underneath it.
   */
  var DIALOGS = ['ccs-msg-ov', 'ccs-apply-ov', 'ccs-color-ov', 'ccs-publish-ov'];

  const anyDialogOpen = function () {
    return DIALOGS.some(function (id) {
      var dialog = document.getElementById(id);
      return dialog && dialog.classList.contains('open');
    });
  };

  /*
   * Left and right step through the colors, when nothing else wants them.
   *
   * The builder already uses the arrows to nudge a selected element, so this
   * only claims them when there is no selection -- and never while a dialog
   * is open or a field has focus, where they mean caret movement.
   *
   * Capture phase, ahead of the builder's own handler on the same node, and
   * stopPropagation so it never sees the key twice.
   */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (!state || switching || anyDialogOpen()) return;
    if (!window.CCSBuilder || !window.CCSBuilder.hasSelection) return;
    if (window.CCSBuilder.hasSelection()) return;

    var target = event.target;
    var typing = target && (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || target.isContentEditable);
    if (typing) return;

    event.preventDefault();
    event.stopPropagation();
    cycleColor(event.key === 'ArrowRight' ? 1 : -1);
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    for (var i = 0; i < DIALOGS.length; i++) {
      var dialog = document.getElementById(DIALOGS[i]);
      if (!dialog || !dialog.classList.contains('open')) continue;
      closeDialog(DIALOGS[i]);
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    /*
     * Nothing open, so this is the builder's own "leave" shortcut -- and on
     * CCS that discards an unpublished design with one keypress and no
     * warning. SIB warns about unsaved work; the admin copy dropped the
     * warning because a proof has nothing to save, and a seller's design very
     * much does.
     *
     * Asked here rather than by restoring the builder's exit dialog, which
     * belongs to a saved-designs feature this build does not have.
     */
    if (!switching && window.CCSBuilder && window.CCSBuilder.hasArtwork()) {
      event.preventDefault();
      event.stopPropagation();
      if (window.confirm(
        'Leave the design studio?\n\n' +
        'This design has not been published yet, and closing loses it.')) {
        window.location.href = EXIT_URL;
      }
    }
  }, true);

  document.addEventListener('input', function (event) {
    if (event.target.dataset && event.target.dataset.ccs === 'markup') refreshQuote();
  });

  /*
   * The rail's "No design yet" is read off state, and state only learns about
   * the canvas when something asks it to. The builder fires this from its own
   * refreshChrome — see the change signal in rederive-ccs.js — so the rail
   * stays honest without polling the canvas.
   */
  document.addEventListener('ccs:builder-changed', function () {
    if (switching || !state) return;
    snapshot();
    renderColors();
  });

  // ── Starting from an existing design ──────────────────────────────────────

  /*
   * Put a saved listing's colors and artwork back on the canvas.
   *
   * This is what "Duplicate" starts from. Every color is re-added with
   * fork:false -- the scenes come from the listing, so forking the one on
   * screen would layer the first color's design under each of the others.
   *
   * A color the garment no longer comes in is skipped rather than failing
   * the whole load: SIB discontinues colors, and the right response is a
   * copy without that one, not a design the seller cannot open. Same for a
   * scene that will not load -- better a color that needs redrawing than a
   * dead page.
   */
  /*
   * `copyOnly` is the difference between Duplicate and Edit.
   *
   * A duplicate becomes a NEW listing, so its files have to be its own: the
   * scenes are seeded but left dirty, and publishing re-exports every one of
   * them. An edit writes over the listing the files came from, so they are
   * already where they belong and only what the seller touches is rebuilt.
   */
  function seedFromListing(listing, copyOnly) {
    var wanted = (listing.colors || []).filter(function (color) {
      return state.colorsByKey[color.key];
    });
    if (!wanted.length) {
      return Promise.reject(new Error(
        'None of the colors in that design are still available on this garment.'));
    }

    // create() has already opened the builder on the style's default color,
    // which the copy may or may not include. Add every color the copy has,
    // THEN drop anything left over -- doing it the other way round would try
    // to remove the last remaining color, which state refuses.
    var seeded = [];
    wanted.forEach(function (color) {
      if (!state.designs[color.key]) {
        var added = State.addColor(state, color.key, { fork: false });
        if (added.error) return;
      }
      seeded.push(color);
    });

    var keep = {};
    seeded.forEach(function (color) { keep[color.key] = true; });
    state.order.slice().forEach(function (key) {
      if (!keep[key]) State.removeColor(state, key);
    });

    state.active.colorKey = state.designs[listing.defaultColorKey]
      ? listing.defaultColorKey
      : state.order[0];
    // The copy opens on what the original opened on. Losing that would be a
    // silent change to a product the seller thought they were duplicating.
    state.defaultColorKey = state.active.colorKey;
    // A copy keeps the original's feature photo too: it is a property of the
    // design, and losing it would be a silent change to what a shopper sees.
    if (listing.featureImage) State.setFeatureImage(state, listing.featureImage);

    return Promise.all(seeded.map(function (color) {
      return Promise.all(SIDES.map(function (side) {
        var stored = color[side];
        if (!stored || !stored.scene) return null;
        return fetch(stored.scene, { cache: 'no-store' })
          .then(function (response) {
            if (!response.ok) throw new Error('scene ' + response.status);
            return response.json();
          })
          .then(function (scene) {
            if (!scene || !scene.canvas) return;
            State.setScene(state, color.key, side, scene.canvas);
            /*
             * Already exported, so it does not need exporting again.
             *
             * setScene marks a side dirty, which is right when the seller
             * draws something and wrong here: this artwork came OUT of the
             * listing and is already on S3. Left dirty, saving an edit would
             * re-render and re-upload all twenty-five colors to reproduce
             * files that already exist -- minutes of waiting to change one.
             *
             * Touching the canvas marks it dirty again, so only what the
             * seller actually changes is rebuilt.
             */
            if (!copyOnly) {
              State.setRendered(state, color.key, side, {
                art: stored.art || null,
                preview: stored.preview || null,
                sceneUrl: stored.scene || null
              });
            }
          })
          .catch(function (error) {
            // Logged, not surfaced: the seller gets a copy with that side
            // blank, which they can see and redraw.
            console.warn('[ccs-builder] scene for ' + color.key + ' ' + side +
              ' could not be loaded', error);
          });
      }));
    }));
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  //
  //   ?style=<sib-handle>   a new design on that garment, blank canvas
  //   ?from=<listing-uid>   a copy of that listing: same garment, same artwork
  //
  // Both land in the same builder; the only difference is what is on it.

  var copying = null;

  /*
   * Boot counts as the host driving the builder, same as a color switch.
   *
   * Without this, the builder's first refreshChrome -- fired while the canvas
   * is still bare, before the restore has put anything on it -- reaches the
   * change listener, which snapshots an empty canvas over the scenes just
   * seeded from the listing. A duplicate then opens blank.
   */
  switching = true;

  // Fetched alongside the style rather than after it: the grid is rendered
  // the first time Add Images opens, which can be before either has landed.
  loadRecent();

  (sourceUid ? Api.getListing(sourceUid) : Promise.resolve(null))
    .then(function (data) {
      copying = data && data.listing;
      if (sourceUid && !copying) throw new Error('That design could not be found.');
      return Api.getStyle(copying ? copying.sourceStyleHandle : styleHandle);
    })
    .then(function (data) {
      style = data;
      state = State.create(style, {
        markupCents: copying && copying.markup != null
          ? Math.round(Number(copying.markup) * 100)
          : 300
      });
      return copying ? seedFromListing(copying, !isEdit) : null;
    })
    .then(function () {
      if (copying) {
        var slider = hook('markup');
        if (slider) slider.value = String(state.markupCents / 100);
        var name = $('#ccs-listing-name');
        // A copy gets a different name, because two products called the same
        // thing in one store is the seller's choice to make rather than the
        // default. An edit keeps its own name -- it IS that product.
        if (name) name.value = isEdit ? copying.name : copying.name + ' copy';
        var description = $('#ccs-listing-desc');
        if (description) description.value = copying.description || '';
      }
      // The dialog says which of the two things Save is about to do.
      if (isEdit) {
        var heading = $('#ccs-publish-ov h3');
        if (heading) heading.textContent = 'Save changes to this design';
        var submit = hook('publish');
        if (submit) submit.textContent = 'Save changes';
        var note = hook('edit-note');
        if (note) note.hidden = false;
      }
      renderRail();
      return showColor(state.active.colorKey);
    })
    .then(function () {
      rebaseline(state.active.colorKey);
      switching = false;
      renderRail();
    })
    .catch(function (error) {
      switching = false;
      message('Shirt Builder',
        (error && error.message) || 'This design could not be started. Please choose a shirt again.');
    });
})(window, document);
